import * as echarts from 'echarts';
import PageCharts from '../page-charts';
import i18n from '@/i18nSetup';
import moment from 'moment';
import comOpt from './common-options.json';
import { merge } from 'lodash';
import utils from "@/utils";

const groupByPeriod = (data, period, name, aggregationType = 'last') => {
  // Clone the data to avoid mutating the original
  const result = [...data];
  
  if (period === 'daily') {
    // Daily view - no additional grouping needed
    return result;
  }
  
  // Group the data by the specified period
  const groupedData = {};
  
  result.forEach(day => {
    let key;
    const date = moment(day.date);
    
    if (period === 'weekly') {
      // Format: "Week X, YYYY" (where X is the week number)
      const weekNum = date.week();
      const year = date.year();
      
      // Get start and end of the week for better date display
      const startOfWeek = moment(date).startOf('week');
      const endOfWeek = moment(date).endOf('week');
      
      key = `${i18n.t('CHART_WEEK')} ${weekNum}, ${year}`;
      
      // Include the date range in the data for tooltip display
      if (!groupedData[key]) {
        groupedData[key] = {
          displayDate: `${startOfWeek.format('MMM DD')} - ${endOfWeek.format('MMM DD')}, ${year}`,
          weekStart: startOfWeek.format('MMM DD'),
          weekEnd: endOfWeek.format('MMM DD'),
          weekYear: year,
          date: startOfWeek.valueOf(), // Use start of week as the date reference
          formatDate: key
        };
      }
    } else if (period === 'monthly') {
      // Format: "MMM YYYY" (e.g., "Jan 2025")
      key = date.format('MMM YYYY');
      
      // For monthly view, get the last day of the month
      const lastDayOfMonth = moment(date).endOf('month');
      const firstDayOfMonth = moment(date).startOf('month');
      
      if (!groupedData[key]) {
        groupedData[key] = {
          displayDate: date.format('MMM YYYY'),
          lastDayOfMonth: lastDayOfMonth.format('MMM DD'),
          date: firstDayOfMonth.valueOf(), // Use start of month as the date reference
          formatDate: key
        };
      }
    }
    
    // For each page index, process metrics based on aggregation type
    const pageCount = 10; // Assume a reasonable maximum number of pages
    for (let i = 0; i < pageCount; i++) {
      const metricKey = `${name}_${i}`;
      
      if (day[metricKey] !== undefined) {
        if (aggregationType === 'sum') {
          // Sum values for aggregation
          groupedData[key][metricKey] = (groupedData[key][metricKey] || 0) + day[metricKey];
        } else if (aggregationType === 'last' || !groupedData[key][metricKey]) {
          // For subscribers, use the last value in the period
          // If the date is more recent, update with this day's metrics and date
          if (!groupedData[key].lastDate || day.date > groupedData[key].lastDate) {
            groupedData[key][metricKey] = day[metricKey];
            groupedData[key].lastDate = day.date;
          }
        }
      }
    }
  });
  
  // Convert the grouped data back to an array and sort by date
  return Object.values(groupedData).sort((a, b) => a.date - b.date);
};

const aggregate = (pages, datesRangePosts, datesRangeStories, name, period = 'daily', aggregationType = 'last') => {
  // Safely get the chart data, ensuring we don't try to access undefined properties
  try {
    const days = PageCharts[name]({ 
      pages, 
      datesRangePosts, 
      datesRangeStories: datesRangeStories || []  // Ensure we always have an array
    }).flat();

    days.forEach(day => {
      day.formatDate = moment(day.date).format('MMM DD');
    });

    const filteredDays = days.filter(day => {
      const essentialKeys = ['date', 'day', 'formatDate'];
      return Object.keys(day).some(key => !essentialKeys.includes(key));
    });

    return period === 'daily' 
      ? filteredDays 
      : groupByPeriod(filteredDays, period, name, aggregationType);
  } catch (error) {
    return [];
  }
};

const create = function ({
  pages,
  datesRangePosts,
  datesRangeStories,
  name,
  ref,
  activePages,
  color,
  type,
  period = 'daily',
  aggregationType = 'last',
  animation = true,
  renderer = 'canvas',
  recreateFilteredPages = null,
}) {
  return new Promise((resolve, reject) => {
    let filteredPages = [...pages];
    
    if (activePages) {
      const hasActivePage = Object.values(activePages).some(isActive => isActive);
      if (hasActivePage) {
        filteredPages = pages.filter(p => activePages[p.hash_id]);
      }
    }
    
    const chart = echarts.init(ref, null, {
      renderer: renderer,
      useDirtyRect: false,
    });

    let actualAggregationType = aggregationType;
    if (name === 'subscribers') {
      actualAggregationType = 'last';
    }

    const data = aggregate(filteredPages, datesRangePosts, datesRangeStories, name, period, actualAggregationType);
    
    // Find minimum and maximum values
    const allValues = data.flatMap(item => filteredPages.map((page, i) => item[`${name}_${i}`]).filter(value => value !== undefined && value !== null));
    const minValue = Math.min(...(allValues.length ? allValues : [0]));
    const maxValue = Math.max(...(allValues.length ? allValues : [100]));

    // Calculate yAxis scaling
    const yAxisMin = minValue * 0.999;
    const yAxisMax = maxValue * 1.001;

    let option = merge({}, comOpt, {
      animation: !!animation,
      animationDuration: animation ? 1000 : 0,
      animationEasing: animation ? 'cubicOut' : 'linear',
      animationThreshold: animation ? 2000 : 0,
      color: [],
      xAxis: {
        type: 'time',
        axisLabel: {
          show: true,
          formatter: function(value) {
            if (period === 'daily') {
              return moment(value).format('MMM DD');
            } else if (period === 'weekly' || period === 'monthly') {
              // For aggregated periods, use the custom label if available
              const dataItem = data.find(item => item.date === value);
              if (dataItem) {
                if (period === 'weekly' && dataItem.displayDate) {
                  return dataItem.displayDate;
                } else if (period === 'monthly' && dataItem.displayDate) {
                  return dataItem.displayDate;
                }
              }
              // Fallback to formatted date
              return moment(value).format('MMM DD');
            }
          }
        },
      },
      yAxis: {
        type: 'value',
        scale: true,
        min: yAxisMin.toFixed(0),
        max: yAxisMax.toFixed(0),
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          show: true,
          snap: true,
          label: { show: false },
          lineStyle: {
            color: '#C2D5FF',
            type: 'line'
          }
        },
        formatter: function (params) {
          if (params.length === 0) return '';
          
          let dateFormatted;
          // Find the data item matching this point
          const paramDate = params[0].data[0];
          const dataItem = data.find(item => item.date.toString() === paramDate.toString());
          
          if (period === 'weekly' && dataItem && dataItem.displayDate) {
            dateFormatted = dataItem.displayDate;
          } else if (period === 'monthly' && dataItem && dataItem.displayDate) {
            dateFormatted = dataItem.displayDate;
          } else {
            // For daily view or fallback
            dateFormatted = moment(paramDate).format('MMM DD, YYYY');
          }
          
          let tooltipHtml = `<div class="tooltip"><span class="date">${dateFormatted}</span><ul class="tooltip-list">`;
          let hasData = false;
          
          if (type === 'overview-one') {
            if (params[0].data[1] != null && params[0].data[1] !== 0) {
              hasData = true;
              tooltipHtml += `<li data-value-lines="${params[0].data[1]}">
                <div class="soc-sea ${filteredPages[0].colorName}">${i18n.t(
                'INTRO_TABS_FOLLOWERS'
              )}</div>${params[0].data[1].toLocaleString('ru-RU')}
              </li>`;
            }
          } else if ((type === 'activity' || type === 'reach') && filteredPages.length === 1) {
            filteredPages.forEach((page, i) => {
              const param = params.find((p) => p.seriesName == i.toString());
              if (param && param.data[1] != null && param.data[1] !== 0) {
                hasData = true;
                tooltipHtml += `<li data-value-lines="${param.data[1]}">
                  <div class="soc-sea ${color.key}"><span class="soc-ico soc-${page.social.toLowerCase()}"></span>${page.owner_name_medium}</div>${param.data[1].toLocaleString('ru-RU')}
                </li>`;
              }
            });
          } else if (
            ((type === 'activity' || type === 'reach') && filteredPages.length > 1) ||
            type === 'overview-many'
          ) {
            filteredPages.forEach((page, i) => {
              const param = params.find((p) => p.seriesName == i.toString());
              if (param && param.data[1] != null) {
                hasData = true;
                tooltipHtml += `<li data-value-lines="${param.data[1]}">
                  <div class="soc-sea ${page.colorName}"><span class="soc-ico soc-${page.social.toLowerCase()}"></span>${page.owner_name_medium}</div>${param.data[1].toLocaleString('ru-RU')}
                </li>`;
              }
            });
          }
          tooltipHtml += '</ul></div>';
          return hasData ? tooltipHtml : '';
        },
      },
      series: [],
    });

    if (type === 'overview-one') {
      option.color = [color.value];
    } else if ((type === 'activity' || type === 'reach') && filteredPages.length === 1) {
      option.color = [color.value];
    }

    if (data.length) {
      filteredPages.forEach((page, i) => {
        if (data.some((item) => item[`${name}_${i}`])) {
          let curPageColor;

          if (((type === 'activity' || type === 'reach') && filteredPages.length > 1) || type === 'overview-many') {
            curPageColor = page.colorCode;
          } else {
            curPageColor = color?.value || page.colorCode || '#000';
          }

          const seriesItem = {
            name: i.toString(),
            type: 'line',
            data: data.map((item) => [item.date, item[`${name}_${i}`]]),
            smooth: 0.4,
            connectNulls: true,
            lineStyle: {
              width: 1,
              color: curPageColor,
            },
            itemStyle: {
              color: curPageColor,
            },
            showSymbol: false,
            symbolSize: 6,
            emphasis: {
              focus: 'series',
              symbol: 'circle',
              itemStyle: {
                color: curPageColor,
                borderColor: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.3)`,
                borderWidth: 10,
                opacity: 1,
              },
            },
          };

          if (filteredPages.length === 1) {
            seriesItem.areaStyle = {
              opacity: 1,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.6)`,
                },
                {
                  offset: 1,
                  color: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.1)`,
                }
              ])
            };
          }

          option.series.push(seriesItem);
        }
      });
    }

    chart.setOption(option);
    resolve(chart);
  });
};

export async function exportChart({
  chart,
  container,
  chartData,
  name,
  type,
  color,
  period,
  activePages,
  exportType = 'svg'
}) {
  if (!chart) {
    return;
  }

  const { pages, datesRangePosts } = chartData;
  const filename = `lines_chart_${exportType === 'csv' ? 'data_' : ''}${new Date().toISOString().split('T')[0]}`;

  if (exportType === 'svg') {
    if (!container) {
      return;
    }

    try {
      const width = container.clientWidth;
      const height = container.clientHeight;
      
      if (chart && typeof chart.dispose === 'function') {
        chart.dispose();
      }
      echarts.dispose(container);
      const svgChart = echarts.init(container, null, { 
        renderer: 'svg',
        animation: false,
        width,
        height
      });
      
      let filteredPages = [...pages];
      if (activePages) {
        const hasActivePage = Object.values(activePages).some(isActive => isActive);
        if (hasActivePage) {
          filteredPages = pages.filter(p => activePages[p.hash_id]);
        }
      }
      
      const aggregateType = name === 'subscribers' ? 'last' : 'sum';
      const data = aggregate(filteredPages, datesRangePosts, null, name, period, aggregateType);
      
      let svgOption = merge({}, comOpt, {
        animation: false,
        color: [],
        xAxis: {
          type: 'time',
          axisLabel: {
            show: true,
            formatter: function(value) {
              if (period === 'daily') {
                return moment(value).format('MMM DD');
              } else if (period === 'weekly' || period === 'monthly') {
                const dataItem = data.find(item => item.date === value);
                if (dataItem && dataItem.displayDate) {
                  return dataItem.displayDate;
                }
                return moment(value).format('MMM DD');
              }
            }
          },
        },
        yAxis: {
          type: 'value',
          scale: true,
        },
        series: [],
      });
      
      // Add series for each filtered page
      if (data.length) {
        filteredPages.forEach((page, i) => {
          const seriesData = data.map(item => {
            const value = item[`${name}_${i}`];
            if (value === undefined || value === null) return null;
            return [item.date, value];
          }).filter(point => point !== null);
          
          if (seriesData.length > 0) {
            let curPageColor;
            if (((type === 'activity' || type === 'reach') && filteredPages.length > 1) || type === 'overview-many') {
              curPageColor = page.colorCode;
            } else {
              curPageColor = color?.value || page.colorCode || '#000';
            }
            
            svgOption.series.push({
              name: i.toString(),
              type: 'line',
              data: seriesData,
              smooth: 0.4,
              connectNulls: true,
              lineStyle: { width: 1, color: curPageColor },
              itemStyle: { color: curPageColor },
              showSymbol: false,
              symbolSize: 6,
            });
            
            if (filteredPages.length === 1) {
              svgOption.series[svgOption.series.length - 1].areaStyle = {
                opacity: 1,
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {
                    offset: 0,
                    color: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.6)`,
                  },
                  {
                    offset: 1,
                    color: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.1)`,
                  }
                ])
              };
            }
          }
        });
      }
      
      // Apply the SVG options
      svgChart.setOption(svgOption);
      
      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get SVG content and download
      const svgElement = container.querySelector('svg');
      if (!svgElement) throw new Error("SVG element not found");
      
      const svgContent = svgElement.outerHTML;
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      utils.downloadFile(blob, `${filename}.svg`);
      
      if (svgChart && typeof svgChart.dispose === 'function') {
        svgChart.dispose();
      }
      
      return null;
    } catch (error) {
      return null;
    }
  } else if (exportType === 'csv') {
    const data = aggregate(pages, datesRangePosts, null, name, period);
    if (data && data.length) {
      let csvContent = "Date";
      pages.forEach((page, i) => {
        csvContent += `;${page.owner_name_medium}`;
      });
      csvContent += "\n";

      data.forEach((item) => {
        let dateDisplay = "";
        
        if (period === 'weekly' && item.displayDate) {
          dateDisplay = item.displayDate;
        } else if (period === 'monthly' && item.displayDate) {
          dateDisplay = item.displayDate;
        } else {
          dateDisplay = moment(item.date).format('YYYY-MM-DD');
        }
        
        csvContent += `${dateDisplay}`;
        
        pages.forEach((page, i) => {
          const value = item[`${name}_${i}`] !== undefined ? utils.formatCsvValue(item[`${name}_${i}`]) : '';
          csvContent += `;${value}`;
        });
        
        csvContent += "\n";
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      utils.downloadFile(blob, `${filename}.csv`);
    }
  }
}

export default create;
